import React from "react";
import moment from "moment";
import { Button, Input, Message } from "semantic-ui-react";
import { Modal } from "../../components";
import { shouldBeInvoiced } from "./SubscriptionUtils";
import PercentageInput from "../../components/PercentageInput";
import Tag from "../../components/Tag/Tag";
import { RadioGroup } from "components/Atoms/inputs/RadioGroup";
import { FilterDatePicker } from "components/DatePicker/SingleDatePicker";
import { findEnumObject } from "utilities/enumutils";
import { AxiosResponse } from "node_modules/axios";
import { reloadOnSuccess } from "Client/utils";
import NumberInput from "components/NumberInput";
import { useValidate, useValidators } from "formvalidation/hooks";
import ValidatorContext from "formvalidation/FormContext";
import { formatLongDate } from "utilities";

interface Props {
  client: server.dto.Client
  activeSubscription: server.dto.Subscription
  nextSubscription: server.dto.NextSubscription
  billingDetails: server.dto.ClientBillingDetails;
  subscriptions: server.dto.Subscription[]
  isOpen: boolean
  onClose: () => void
}

interface TypeOptionProps {
  activeSubscription: server.dto.Subscription
  type: server.dto.SubscriptionTypes
  selectedType: server.dto.SubscriptionType
  selectedBillingCycle: server.dto.BillingCycle
  hasBillingCycle: boolean
  discount: number
  enabledInternalUsers: number
  internalUsers: number
  subscriptionAction: SubscriptionAction
  discountReason: string
  trialEndDate: moment.Moment

  discountChanged: (percentage: number) => void
  internalUsersChanged: (userCount: number) => void
  onBillingCycleChanged: (cycle: server.dto.BillingCycle) => void
  onSubscriptionActionChanged: (action: SubscriptionAction) => void
  onDiscountReasonChanged: (reason: string) => void
  onTrialEndDateChanged: (moment: moment.Moment) => void
}

type SubscriptionAction = "replace" | "startNew";

const TypeOption = (props: TypeOptionProps) => {
  const isActive = props.selectedType.value === props.type;

  const displayBillingCycle = props.hasBillingCycle && isActive;

  const displayUsers = props.type === "paid" && isActive;

  const displayDiscount = props.hasBillingCycle && isActive;

  const isTrial = props.type === "trial" && isActive;

  const internalUserValidation = useValidate({
    key: "internalUsers",
    value: props.internalUsers,
    dependencies: [displayUsers, props.internalUsers],
    title: `"Internal users must be more than the number of users currently active"`,
    validate: async (value) => !displayUsers || props.activeSubscription.type === "trial" || props.activeSubscription.type === "trialEnd" || (value >= props.enabledInternalUsers),
  })

  const discountValidation = useValidate({
    key: "discountPercentage",
    value: props.discount,
    dependencies: [displayDiscount, props.discount],
    title: "Discount percentage must be between 0% and 100%",
    validate: async (value) => !displayDiscount || (value >= 0 && value < 1),
  })

  const hasSelectedActionValidation = useValidate({
    key: "subscriptionAction",
    value: props.subscriptionAction,
    dependencies: [isActive, props.subscriptionAction],
    title: "Please select an action",
    validate: async (value) => !isActive || value != null,
  })

  // If the active subscription starts today, we only want to show the "change current subscription" option
  const displayEndAndStartNewSubscriptionOption = moment.utc(props.activeSubscription.start).startOf("day").valueOf() !== moment.utc().startOf("day").valueOf();
  const displayEndAndStartRadioOptions = displayEndAndStartNewSubscriptionOption ? [{
    label: "Adjust running subscription",
    value: "replace"
  }, {
    label: "End running subscription and start new subscription today",
    value: "startNew"
  }] : [{
    label: "Adjust running subscription",
    value: "replace"
  }];

  return <div className="margin-left-large">
    <table className="s1_table clean rowspace">
      <tbody>
        {isActive && <tr>
          <td>
            action:
          </td>
          <td>
            <RadioGroup
              orientation="vertical"
              options={displayEndAndStartRadioOptions}
              value={props.subscriptionAction}
              onChange={v => props.onSubscriptionActionChanged(v as SubscriptionAction)}
              error={hasSelectedActionValidation.error.hasError}
            />
          </td>
        </tr>}

        {displayBillingCycle && <tr>
          <td>
            billing cycle:
          </td>
          <td>
            <RadioGroup
              orientation="vertical"
              options={serverReference.billingCycles.map(cycle => ({ label: cycle.text, value: cycle.value }))}
              value={props.selectedBillingCycle.value}
              onChange={v => props.onBillingCycleChanged(findEnumObject(v, serverReference.billingCycles))}
            />
          </td>
        </tr>}

        {isTrial && <tr>
          <td>
            trial end date:
          </td>
          <td>
            <FilterDatePicker
              minDate={moment.utc().startOf("day")}
              maxDate={moment.utc().add(10, "year")}
              value={props.trialEndDate}
              onChange={props.onTrialEndDateChanged}
            />
          </td>
        </tr>}

        {displayDiscount && <>
          <tr>
            <td>
              discount:
            </td>
            <td className="flex align-center">
              <div style={{ width: 50 }} className="margin-right-huge">
                {/* The discount percentage can never be 100. The input box should only allow for numbers with a decimal. Account for culture differences, where separator is a comma */}
                <PercentageInput
                  value={props.discount}
                  onChange={props.discountChanged}
                  canBeNegative={false}
                  digitsAfterDecimalPoint={2}
                  error={discountValidation.error.hasError}
                  enablePercentageLabel
                />
              </div>
              {discountValidation.error.hasError && <div className="s1_red s1_small">
                cannot be 100% or higher
              </div>}
            </td>
          </tr>
          <tr>
            <td>
              discount reason:
            </td>
            <td>
              <Input value={props.discountReason} fluid onChange={(_, { value }) => { props.onDiscountReasonChanged(value); }} maxLength={serverReference.constants.client.maxDiscountReasonLength} placeholder="This text will appear on the invoice" />
            </td>
          </tr>
        </>}

        {displayUsers && <tr>
          <td>
            internal user(s):
          </td>
          <td className="flex">
            <div style={{ width: 50 }} className="margin-right">
              <NumberInput
                value={props.internalUsers}
                error={internalUserValidation.error.hasError}
                canBeNegative={false}
                decimalPoints={0}
                onChanged={val => { props.internalUsersChanged(val.value); }}
                width={50}
              />
            </div>
            {internalUserValidation.error.hasError && <div className="s1_red s1_small">
              cannot be less than the number of users currently active: {props.enabledInternalUsers} user(s).
            </div>}
          </td>
        </tr>}
      </tbody>
    </table>
  </div>;
};

const ChangeActiveSubscriptionModal = (props: Props) => {
  const [selectedType, setSelectedType] = React.useState<server.dto.SubscriptionType>(findEnumObject(props.activeSubscription.type, serverReference.subscriptionTypes));
  const [selectedCycle, setSelectedCycle] = React.useState<server.dto.BillingCycle>(findEnumObject(props.activeSubscription.billingCycle, serverReference.billingCycles));
  const [isSaving, setIsSaving] = React.useState<boolean>(false);
  const [internalUsers, setInternalUsers] = React.useState<number>(props.activeSubscription.internalUsers);
  const [discount, setDiscount] = React.useState<number>(props.activeSubscription.discount.value);
  const [discountReason, setDiscountReason] = React.useState<string>(props.activeSubscription.discountReason || "");
  const [subscriptionAction, setSubscriptionAction] = React.useState<SubscriptionAction>(undefined);
  const [trialEndDate, setTrialEndDate] = React.useState<moment.Moment>(moment.utc().add(7, "days").startOf("day"));

  const subscriptionTypeValues: server.dto.SubscriptionTypes[] = ["trial", "free", "paid", "readOnly", "cancelledByClient", "cancelledBySystemOne"];

  const hasBillingCycles = (type: server.dto.SubscriptionTypes) => type === "paid" || type === "readOnly";

  const onSubscriptionTypeChanged = (type: server.dto.SubscriptionTypes) => {
    setSelectedType(findEnumObject(type, serverReference.subscriptionTypes));

    // If we already selected a billing cycle, we want to keep it
    // If no billing cycle was selected, we want to select the first one
    // If the new type doesn't have billing cycles, we want to remove the selected cycle
    if (hasBillingCycles(type) && selectedCycle == null) {
      setSelectedCycle(serverReference.billingCycles[0]);
    } else if (!hasBillingCycles(type)) {
      setSelectedCycle(undefined);
    }
    setDiscount(props.activeSubscription.discount.value);
    setInternalUsers(props.activeSubscription.internalUsers);
    setSubscriptionAction(undefined);
  };

  const onSaveClicked = async (e) => {
    e.preventDefault();

    const validationRes = await validators.validateAll();
    if (validationRes.hasErrors) {
      return;
    }

    if (isSaving) {
      return;
    }
    setIsSaving(true);

    try {
      const subscriptionPayload: server.dto.ChangeActiveSubscriptionInstruction = {
        clientId: props.client.clientId,
        subscriptionType: selectedType.value,
        billingCycle: selectedCycle != null ? selectedCycle.value : undefined,
        // subscriptionStart: subscriptionAction === "startNew" ? new Date() : undefined,
        internalUsers,
        discountPercentage: discount != null ? discount : undefined,
        discountReason,
        subscriptionEnd: selectedType.value === "trial" ? trialEndDate.toDate() : undefined
      };
      let res: AxiosResponse<server.dto.ApiResponse>;
      if (subscriptionAction === "replace") {
        res = await window.CoreApi.Client.UpdateActiveSubscription(subscriptionPayload);
      } else if (subscriptionAction === "startNew") {
        res = await window.CoreApi.Client.EndActiveAndStartNewSubscription(subscriptionPayload);
      }

      reloadOnSuccess(res);
    } catch (ex) {
      alert("Something went wrong...: " + ex);
      setIsSaving(false);
    }
  };

  const subStartDate = moment.utc(props.activeSubscription.start);
  const daysDifference = moment.utc().diff(subStartDate, "days");
  const displaySubscriptionWarning = daysDifference > 14 && subscriptionAction === "replace";

  const validators = useValidators();

  return <Modal
    width="small"
    title={"Modify running subscription"}
    handleClose={props.onClose}
    isOpen={props.isOpen}
  >
    <ValidatorContext settings={validators.validatorSettings}>
      <div className="grow1 scrollbox-auto margin-bottom-larger">
        <RadioGroup
          orientation="vertical"
          options={subscriptionTypeValues.map(type => {
            const subscriptionType = findEnumObject(type, serverReference.subscriptionTypes);
            return ({
              value: type,
              label: <Tag
                label={subscriptionType.internalText}
                size={"larger"}
                color={subscriptionType.hexColor} />,
              renderOnSelect: () => <TypeOption
                activeSubscription={props.activeSubscription}
                hasBillingCycle={hasBillingCycles(type)}
                type={type}
                selectedType={selectedType}
                selectedBillingCycle={selectedCycle}
                internalUsers={internalUsers}
                enabledInternalUsers={props.nextSubscription.internalUsers}
                discount={discount}
                discountChanged={setDiscount}
                internalUsersChanged={setInternalUsers}
                subscriptionAction={subscriptionAction}
                onSubscriptionActionChanged={setSubscriptionAction}
                discountReason={discountReason}
                onDiscountReasonChanged={setDiscountReason}
                trialEndDate={trialEndDate}
                onTrialEndDateChanged={setTrialEndDate}
                onBillingCycleChanged={setSelectedCycle}
              />
            });
          })}
          value={selectedType.value}
          onChange={(value) => onSubscriptionTypeChanged(value as server.dto.SubscriptionTypes)}
        />
      </div>

      <div className="margin-bottom-large">
        {(displaySubscriptionWarning || shouldBeInvoiced(props.activeSubscription.type)) && <Message color="red">
          {displaySubscriptionWarning && <p>You want to change the existing subscription that started on {formatLongDate(props.activeSubscription.start)} ({daysDifference} days ago).<br />
            {"Are you sure that you don't want to start a new subscription instead, so we keep the history?"}</p>}

          {shouldBeInvoiced(props.activeSubscription.type) && <p>{"An invoice might exist for this subscription. Don't forget to change the invoice manually."}</p>}
        </Message>}
      </div>

      <div className="flex justify-end">
        <div><Button disabled={isSaving} primary loading={isSaving} onClick={onSaveClicked}>{RESX.Buttons.BtnOk}</Button></div>
      </div>
    </ValidatorContext>
  </Modal>;
};

export default ChangeActiveSubscriptionModal;